import React, { useState, useEffect } from 'react';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import { Search, Filter, Users, LayoutTemplate, ArrowUp, ArrowDown, ChevronLeft, ChevronRight, ArrowLeft, BadgeCheck, ShieldCheck } from 'lucide-react';
import BriefDetailModal, { BriefDetailType } from '../../components/Profile/brief/BriefDetailModal';

import { discoverService } from '../../services/discoverService';
import { Creator, Brief } from '../../types/discover';
import { DEFAULT_AVATAR_URL } from '../../constants/assets';
import defaultCover from '../../assets/images/clouds2.png';
import { SelectTags, type NewTagState } from '../../components/Tags/SelectTags';
import { ALL_LEAF_CATEGORIES, STYLE_FACETS } from '../../constants/tags';
import { FollowButton } from '../../components/UI/FollowButton';

// Remove local MOCK data as it is now in service
// Local mocks removed. Using service.

type DiscoverMode = 'creators' | 'briefs';

const Discover = () => {
    const location = useLocation();
    const [searchParams, setSearchParams] = useSearchParams();
    const mode: DiscoverMode = (searchParams.get('tab') as DiscoverMode) || (location.state as any)?.mode || 'creators';
    const setMode = (m: DiscoverMode) => setSearchParams({ tab: m }, { replace: true });

    useEffect(() => {
        const state = location.state as any;
        if (state?.mode && !searchParams.get('tab')) {
            setSearchParams({ tab: state.mode }, { replace: true });
        }
        if (state?.initialTags && Array.isArray(state.initialTags)) {
            // Convert legacy flat tag format to NewTagState
            const newTagState: NewTagState = {
                primaryCategory: null,
                secondaryCategories: [],
                toolTags: [],
                styleFacets: [],
            };
            const leafNameToId: Record<string, string> = {};
            ALL_LEAF_CATEGORIES.forEach(c => { leafNameToId[c.name] = c.id; });
            const facetNameToId: Record<string, string> = {};
            STYLE_FACETS.forEach(f => { facetNameToId[f.name] = f.id; });

            state.initialTags.forEach((tag: { type: string, name: string }) => {
                if (tag.type === 'category') {
                    const leafId = leafNameToId[tag.name];
                    if (leafId) {
                        if (!newTagState.primaryCategory) {
                            newTagState.primaryCategory = leafId;
                        } else if (newTagState.secondaryCategories.length < 2) {
                            newTagState.secondaryCategories.push(leafId);
                        }
                    }
                } else if (tag.type === 'tool') {
                    newTagState.toolTags.push(tag.name);
                } else if (tag.type === 'style') {
                    const facetId = facetNameToId[tag.name];
                    if (facetId && !newTagState.styleFacets.includes(facetId)) {
                        newTagState.styleFacets.push(facetId);
                    }
                }
            });
            setTagState(newTagState);
        }
    }, [location.state]);

    const [searchQuery, setSearchQuery] = useState('');


    // Phase 1: New layered tag state
    const [tagState, setTagState] = useState<NewTagState>({
        primaryCategory: null,
        secondaryCategories: [],
        toolTags: [],
        styleFacets: [],
    });

    // Build flat tag names for filtering items
    const selectedTagNames = React.useMemo(() => {
        const names: string[] = [];
        if (tagState.primaryCategory) {
            const leaf = ALL_LEAF_CATEGORIES.find(c => c.id === tagState.primaryCategory);
            if (leaf) names.push(leaf.name);
        }
        tagState.secondaryCategories.forEach(id => {
            const leaf = ALL_LEAF_CATEGORIES.find(c => c.id === id);
            if (leaf) names.push(leaf.name);
        });
        tagState.styleFacets.forEach(id => {
            const facet = STYLE_FACETS.find(f => f.id === id);
            if (facet) names.push(facet.name);
        });
        names.push(...tagState.toolTags);
        return names;
    }, [tagState]);

    const [sortBy, setSortBy] = useState<string>('default');
    const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('desc');
    const [currentPage, setCurrentPage] = useState(1);
    const [itemsPerPage, setItemsPerPage] = useState(12);
    const [showFilters, setShowFilters] = useState(false);
    const [creatorFilters, setCreatorFilters] = useState({
        verified: false,
        enterprise: false,
        type: 'all' as 'all' | 'personal' | 'studio',
        ratingRange: 'all' as 'all' | 'five' | 'fourPlus'
    });

    const [briefFilters, setBriefFilters] = useState({
        budgetMin: '',
        budgetMax: '',
        durationRange: 'all' as 'all' | 'short' | 'medium' | 'long' | 'extreme',
        clientVerified: false,
        clientEnterprise: false
    });

    const [selectedBrief, setSelectedBrief] = useState<BriefDetailType | null>(null);

    const [creators, setCreators] = useState<Creator[]>([]);
    const [briefs, setBriefs] = useState<Brief[]>([]);
    const [loading, setLoading] = useState(true);

    const navigate = useNavigate();

    const handleBriefClick = (brief: Brief) => {
        const detail: BriefDetailType = {
            id: brief.id,
            title: brief.title,
            description: brief.description || "这是一个来自发现页的需求详情。",
            deliverables: brief.deliverables || "未指定交付内容",
            budgetRange: brief.budget,
            deliveryTimeRange: brief.expectedDurationDays ? `${brief.expectedDurationDays}天` : '可谈',
            communicationCount: brief.communicationCount || 0,
            trialCount: brief.trialCount || 0,
            viewCount: brief.viewCount || 0,
            tags: brief.tags || [],
            exampleWorks: [],
            targetDate: brief.deadlineDate || ""
        };
        setSelectedBrief(detail);
    };

    useEffect(() => {
        const loadData = async () => {
            setLoading(true);
            try {
                const [fetchedCreators, fetchedBriefs] = await Promise.all([
                    discoverService.getCreators({ limit: 200 }),
                    discoverService.getBriefs({})
                ]);
                setCreators(fetchedCreators);
                setBriefs(fetchedBriefs);
            } catch (err) {
                console.error(err);
            } finally {
                setLoading(false);
            }
        };
        loadData();
    }, []);

    // Helpers
    const parseMetric = (val: string) => {
        if (val.includes('k')) return parseFloat(val) * 1000;
        if (val.includes('w')) return parseFloat(val) * 10000;
        return parseFloat(val);
    };


    const sortedAndPaginatedItems = () => {
        // Use state instead of MOCK
        let items: any[] = mode === 'creators' ? [...creators] : [...briefs];

        // 1. Filter
        if (mode === 'creators') {
            items = items.filter((creator: any) => {
                const matchesSearch = creator.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
                    creator.handle.toLowerCase().includes(searchQuery.toLowerCase());
                const hasMatchingTag = selectedTagNames.length === 0 || selectedTagNames.some(st => creator.tags?.includes(st));

                // New Filters
                const matchesVerified = creatorFilters.verified ? creator.verified : true;
                const matchesEnterprise = creatorFilters.enterprise ? creator.enterpriseVerified : true;
                const matchesType = creatorFilters.type === 'all' ? true : creator.type === creatorFilters.type;
                const matchesRating = creatorFilters.ratingRange === 'all'
                    ? true
                    : creatorFilters.ratingRange === 'five'
                        ? creator.reviewCount > 0 && creator.rating >= 5
                        : creator.reviewCount > 0 && creator.rating >= 4;

                return matchesSearch && hasMatchingTag && matchesVerified && matchesEnterprise && matchesType && matchesRating;
            });
        } else {
            items = items.filter((brief: any) => {
                const matchesSearch = brief.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
                    brief.client.toLowerCase().includes(searchQuery.toLowerCase());
                const hasMatchingTag = selectedTagNames.length === 0 || selectedTagNames.some(st => brief.tags?.includes(st));

                // New Filters
                const budgetMin = briefFilters.budgetMin ? Number(briefFilters.budgetMin) : 0;
                const budgetMax = briefFilters.budgetMax ? Number(briefFilters.budgetMax) : Infinity;
                const matchesBudget = (brief.numericBudget || 0) >= budgetMin && (brief.numericBudget || 0) <= budgetMax;

                let matchesDuration = true;
                const days = brief.expectedDurationDays || 0;
                if (briefFilters.durationRange === 'short') matchesDuration = days < 3;
                else if (briefFilters.durationRange === 'medium') matchesDuration = days >= 3 && days <= 7;
                else if (briefFilters.durationRange === 'long') matchesDuration = days > 7 && days <= 30;
                else if (briefFilters.durationRange === 'extreme') matchesDuration = days > 30;

                const matchesClientVerified = briefFilters.clientVerified ? brief.clientVerified : true;
                const matchesClientEnterprise = briefFilters.clientEnterprise ? brief.clientEnterpriseVerified : true;

                return matchesSearch && hasMatchingTag && matchesBudget && matchesDuration && matchesClientVerified && matchesClientEnterprise;
            });
        }

        // 2. Sort
        // Creators: always bucket "blank" profiles (no works, no tags, no
        // cover, no reviews) to the end regardless of sort mode. Inside
        // each bucket the user-selected order applies.
        const isBlankCreator = (c: any): boolean => {
            const noWorks = !c.pinnedWorks || c.pinnedWorks.length === 0;
            const noTags = !c.tags || c.tags.length === 0;
            const noCover = !c.cover;
            const noReviews = !c.reviewCount || c.reviewCount === 0;
            return noWorks && noTags && noCover && noReviews;
        };

        const compareBySortField = (a: any, b: any): number => {
            let valA: number | undefined;
            let valB: number | undefined;
            if (mode === 'creators') {
                if (sortBy === 'followers') {
                    valA = parseMetric(a.followers);
                    valB = parseMetric(b.followers);
                } else if (sortBy === 'likes') {
                    valA = parseMetric(a.likes);
                    valB = parseMetric(b.likes);
                } else if (sortBy === 'joinDate') {
                    valA = new Date(a.joinDate).getTime();
                    valB = new Date(b.joinDate).getTime();
                } else if (sortBy === 'rating') {
                    valA = a.rating || 0;
                    valB = b.rating || 0;
                }
            } else {
                if (sortBy === 'budget') {
                    valA = a.numericBudget;
                    valB = b.numericBudget;
                } else if (sortBy === 'deadline') {
                    valA = new Date(a.deadlineDate).getTime();
                    valB = new Date(b.deadlineDate).getTime();
                } else if (sortBy === 'createTime') {
                    valA = new Date(a.createTime).getTime();
                    valB = new Date(b.createTime).getTime();
                }
            }
            if (valA === undefined || valB === undefined) return 0;
            if (valA < valB) return sortOrder === 'asc' ? -1 : 1;
            if (valA > valB) return sortOrder === 'asc' ? 1 : -1;
            return 0;
        };

        if (mode === 'creators') {
            items.sort((a, b) => {
                const blankA = isBlankCreator(a);
                const blankB = isBlankCreator(b);
                if (blankA !== blankB) return blankA ? 1 : -1; // blanks last
                if (sortBy === 'default') return 0;
                return compareBySortField(a, b);
            });
        } else if (sortBy !== 'default') {
            items.sort(compareBySortField);
        }

        // 3. Paginate
        const totalItems = items.length;
        const totalPages = Math.ceil(totalItems / itemsPerPage);
        const startIndex = (currentPage - 1) * itemsPerPage;
        const paginated = items.slice(startIndex, startIndex + itemsPerPage);

        return { paginated, totalItems, totalPages };
    };

    const { paginated: displayItems, totalPages } = sortedAndPaginatedItems();

    useEffect(() => {
        setCurrentPage(1);
    }, [mode, searchQuery, selectedTagNames, itemsPerPage, sortBy, sortOrder, creatorFilters, briefFilters]);

    if (loading) {
        return (
            <div className="min-h-screen bg-gray-50 pt-24 pb-20 flex justify-center items-start">
                <div className="mt-20 animate-spin rounded-full h-8 w-8 border-b-2 border-black"></div>
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-[#FDFDFD] pt-24 pb-20">
            <div className="max-w-[1440px] mx-auto px-6 md:px-12">

                {/* Header & Controls */}
                <div className="mb-8 space-y-6">
                    <div>
                        <button
                            onClick={() => navigate(-1)}
                            className="flex items-center gap-2 text-gray-500 hover:text-gray-900 transition-colors mb-4 group w-fit"
                        >
                            <ArrowLeft className="w-4 h-4 transition-transform group-hover:-translate-x-1" />
                            <span className="text-sm font-medium">返回</span>
                        </button>
                        <h1 className="text-4xl font-bold text-gray-900 tracking-tight flex items-center gap-3 mb-2">发现</h1>
                    </div>

                    <div className="flex flex-col md:flex-row gap-4 justify-between items-start md:items-center">
                        {/* Mode Toggle */}
                        <div className="bg-gray-200 p-1 rounded-xl flex shrink-0">
                            <button
                                onClick={() => setMode('creators')}
                                className={`px-6 py-2 rounded-lg font-medium text-sm transition-all flex items-center gap-2 ${mode === 'creators' ? 'bg-white shadow-sm text-black' : 'text-gray-600 hover:text-black'}`}
                            >
                                <Users className="w-4 h-4" />
                                找创作者
                            </button>
                            <button
                                onClick={() => setMode('briefs')}
                                className={`px-6 py-2 rounded-lg font-medium text-sm transition-all flex items-center gap-2 ${mode === 'briefs' ? 'bg-white shadow-sm text-black' : 'text-gray-600 hover:text-black'}`}
                            >
                                <LayoutTemplate className="w-4 h-4" />
                                浏览需求
                            </button>
                        </div>

                        {/* Search & Filter Trigger */}
                        <div className="flex gap-3 w-full md:w-auto">
                            <div className="relative flex-1 md:w-80">
                                <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                                <input
                                    type="text"
                                    placeholder={mode === 'creators' ? "搜索昵称、ID..." : "搜索需求标题、客户..."}
                                    value={searchQuery}
                                    onChange={(e) => setSearchQuery(e.target.value)}
                                    className="w-full pl-12 pr-4 py-3 bg-white border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-black transition-all"
                                />
                            </div>
                            <button
                                onClick={() => setShowFilters(!showFilters)}
                                className={`px-4 py-3 border border-gray-200 bg-white rounded-xl hover:bg-gray-50 transition-colors flex items-center gap-2 ${selectedTagNames.length > 0 ? 'border-black text-black' : 'text-gray-600'}`}
                            >
                                <Filter className="w-5 h-5" />
                                <span className="hidden md:inline">筛选</span>
                                {selectedTagNames.length > 0 && (
                                    <span className="bg-black text-white text-xs px-2 py-0.5 rounded-full">{selectedTagNames.length}</span>
                                )}
                            </button>
                        </div>
                    </div>

                    {/* Filter Panel (Expandable) */}
                    {showFilters && (
                        <div className="bg-white p-6 rounded-2xl border border-gray-100 shadow-sm animate-fade-in space-y-8">

                            {/* Tags Section */}
                            <div>
                                <div className="flex justify-between items-center mb-4">
                                    <h3 className="font-bold text-gray-900">按标签筛选</h3>
                                    <button
                                        onClick={() => {
                                            setTagState({ primaryCategory: null, secondaryCategories: [], toolTags: [], styleFacets: [] });
                                            setCreatorFilters({ verified: false, enterprise: false, type: 'all', ratingRange: 'all' });
                                            setBriefFilters({
                                                budgetMin: '',
                                                budgetMax: '',
                                                durationRange: 'all',
                                                clientVerified: false,
                                                clientEnterprise: false
                                            });
                                        }}
                                        className="text-sm text-gray-500 hover:text-black"
                                    >
                                        重置所有
                                    </button>
                                </div>
                                <div className="space-y-6">
                                    <SelectTags
                                        value={tagState}
                                        onChange={setTagState}
                                        filterMode={true}
                                    />
                                </div>
                            </div>

                            <div className="border-t border-gray-100 pt-6"></div>

                            {/* Conditional Advanced Filters */}
                            {mode === 'creators' ? (
                                <div className="space-y-4">
                                    <h3 className="font-bold text-gray-900">创作者筛选</h3>
                                    <div className="grid gap-4 xl:grid-cols-3 md:grid-cols-2">
                                        <div className="rounded-2xl border border-gray-100 bg-gray-50/70 p-4">
                                            <h4 className="text-sm font-bold text-gray-500 mb-3">用户类型</h4>
                                            <div className="flex flex-wrap gap-2">
                                                {[
                                                    { value: 'all', label: '全部' },
                                                    { value: 'personal', label: '个人创作者' },
                                                    { value: 'studio', label: '工作室/机构' }
                                                ].map(option => (
                                                    <button
                                                        key={option.value}
                                                        type="button"
                                                        onClick={() => setCreatorFilters(prev => ({ ...prev, type: option.value as 'all' | 'personal' | 'studio' }))}
                                                        className={`px-3 py-1.5 rounded-full text-xs font-medium border transition-all ${creatorFilters.type === option.value
                                                            ? 'bg-black text-white border-black'
                                                            : 'bg-white text-gray-600 border-gray-200 hover:border-gray-300'
                                                            }`}
                                                    >
                                                        {option.label}
                                                    </button>
                                                ))}
                                            </div>
                                        </div>

                                        <div className="rounded-2xl border border-gray-100 bg-gray-50/70 p-4">
                                            <h4 className="text-sm font-bold text-gray-500 mb-3">认证状态</h4>
                                            <div className="flex flex-wrap gap-2">
                                                <button
                                                    type="button"
                                                    onClick={() => setCreatorFilters(prev => ({ ...prev, verified: !prev.verified }))}
                                                    className={`px-3 py-1.5 rounded-full text-xs font-medium border transition-all ${creatorFilters.verified
                                                        ? 'bg-black text-white border-black'
                                                        : 'bg-white text-gray-600 border-gray-200 hover:border-gray-300'
                                                        }`}
                                                >
                                                    已实名认证
                                                </button>
                                                <button
                                                    type="button"
                                                    onClick={() => setCreatorFilters(prev => ({ ...prev, enterprise: !prev.enterprise }))}
                                                    className={`px-3 py-1.5 rounded-full text-xs font-medium border transition-all ${creatorFilters.enterprise
                                                        ? 'bg-black text-white border-black'
                                                        : 'bg-white text-gray-600 border-gray-200 hover:border-gray-300'
                                                        }`}
                                                >
                                                    已企业认证
                                                </button>
                                            </div>
                                        </div>

                                        <div className="rounded-2xl border border-gray-100 bg-gray-50/70 p-4 md:col-span-2 xl:col-span-1">
                                            <h4 className="text-sm font-bold text-gray-500 mb-3">评分区间</h4>
                                            <div className="flex flex-wrap gap-2">
                                                {[
                                                    { value: 'all', label: '全部评分' },
                                                    { value: 'five', label: '只看5分' },
                                                    { value: 'fourPlus', label: '4分以上' }
                                                ].map(option => (
                                                    <button
                                                        key={option.value}
                                                        type="button"
                                                        onClick={() => setCreatorFilters(prev => ({ ...prev, ratingRange: option.value as 'all' | 'five' | 'fourPlus' }))}
                                                        className={`px-3 py-1.5 rounded-full text-xs font-medium border transition-all ${creatorFilters.ratingRange === option.value
                                                            ? 'bg-black text-white border-black'
                                                            : 'bg-white text-gray-600 border-gray-200 hover:border-gray-300'
                                                            }`}
                                                    >
                                                        {option.label}
                                                    </button>
                                                ))}
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            ) : (
                                <div className="space-y-4">
                                    <h3 className="font-bold text-gray-900">需求筛选</h3>
                                    <div className="grid gap-4 xl:grid-cols-3 md:grid-cols-2">
                                        <div className="rounded-2xl border border-gray-100 bg-gray-50/70 p-4">
                                            <h4 className="text-sm font-bold text-gray-500 mb-3">预算区间 (CNY)</h4>
                                            <div className="flex items-center gap-3">
                                                <input
                                                    type="number"
                                                    placeholder="最小"
                                                    value={briefFilters.budgetMin}
                                                    onChange={(e) => setBriefFilters(prev => ({ ...prev, budgetMin: e.target.value }))}
                                                    className="w-32 px-3 py-2 bg-white border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-1 focus:ring-black"
                                                />
                                                <span className="text-gray-400">-</span>
                                                <input
                                                    type="number"
                                                    placeholder="最大"
                                                    value={briefFilters.budgetMax}
                                                    onChange={(e) => setBriefFilters(prev => ({ ...prev, budgetMax: e.target.value }))}
                                                    className="w-32 px-3 py-2 bg-white border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-1 focus:ring-black"
                                                />
                                            </div>
                                        </div>

                                        <div className="rounded-2xl border border-gray-100 bg-gray-50/70 p-4">
                                            <h4 className="text-sm font-bold text-gray-500 mb-3">预计工期</h4>
                                            <div className="flex flex-wrap gap-2">
                                                {[
                                                    { value: 'all', label: '不限' },
                                                    { value: 'short', label: '< 3天' },
                                                    { value: 'medium', label: '3-7天' },
                                                    { value: 'long', label: '7-30天' },
                                                    { value: 'extreme', label: '> 30天' }
                                                ].map(opt => (
                                                    <button
                                                        key={opt.value}
                                                        type="button"
                                                        onClick={() => setBriefFilters(prev => ({ ...prev, durationRange: opt.value as any }))}
                                                        className={`px-3 py-1.5 rounded-full text-xs font-medium border transition-all ${briefFilters.durationRange === opt.value
                                                            ? 'bg-black text-white border-black'
                                                            : 'bg-white text-gray-600 border-gray-200 hover:border-gray-300'
                                                            }`}
                                                    >
                                                        {opt.label}
                                                    </button>
                                                ))}
                                            </div>
                                        </div>

                                        <div className="rounded-2xl border border-gray-100 bg-gray-50/70 p-4 md:col-span-2 xl:col-span-1">
                                            <h4 className="text-sm font-bold text-gray-500 mb-3">发布方认证</h4>
                                            <div className="flex flex-wrap gap-2">
                                                <button
                                                    type="button"
                                                    onClick={() => setBriefFilters(prev => ({ ...prev, clientVerified: !prev.clientVerified }))}
                                                    className={`px-3 py-1.5 rounded-full text-xs font-medium border transition-all ${briefFilters.clientVerified
                                                        ? 'bg-black text-white border-black'
                                                        : 'bg-white text-gray-600 border-gray-200 hover:border-gray-300'
                                                        }`}
                                                >
                                                    已实名认证
                                                </button>
                                                <button
                                                    type="button"
                                                    onClick={() => setBriefFilters(prev => ({ ...prev, clientEnterprise: !prev.clientEnterprise }))}
                                                    className={`px-3 py-1.5 rounded-full text-xs font-medium border transition-all ${briefFilters.clientEnterprise
                                                        ? 'bg-black text-white border-black'
                                                        : 'bg-white text-gray-600 border-gray-200 hover:border-gray-300'
                                                        }`}
                                                >
                                                    已企业认证
                                                </button>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            )}
                        </div>
                    )}
                </div>

                {/* Sort & Pagination Controls */}
                <div className="flex flex-wrap justify-between items-center mb-6 gap-4">
                    <div className="flex items-center gap-2">
                        <span className="text-sm text-gray-500">排序:</span>
                        <select
                            value={sortBy}
                            onChange={(e) => setSortBy(e.target.value)}
                            className="bg-transparent text-sm font-medium text-gray-900 focus:outline-none cursor-pointer"
                        >
                            <option value="default">默认</option>
                            {mode === 'creators' ? (
                                <>
                                    <option value="likes">获赞数</option>
                                    <option value="followers">粉丝数</option>
                                    <option value="rating">评分</option>
                                    <option value="joinDate">加入时间</option>
                                </>
                            ) : (
                                <>
                                    <option value="createTime">最新发布</option>
                                    <option value="deadline">截止日期</option>
                                    <option value="budget">预算金额</option>
                                </>
                            )}
                        </select>
                        <button
                            onClick={() => setSortOrder(prev => prev === 'asc' ? 'desc' : 'asc')}
                            className="p-1 rounded hover:bg-gray-100 transition-colors"
                            title={sortOrder === 'asc' ? "升序" : "降序"}
                        >
                            {sortOrder === 'asc' ? <ArrowUp className="w-4 h-4 text-gray-600" /> : <ArrowDown className="w-4 h-4 text-gray-600" />}
                        </button>
                    </div>

                    <div className="flex items-center gap-2">
                        <span className="text-sm text-gray-500">每页显示:</span>
                        <select
                            value={itemsPerPage}
                            onChange={(e) => setItemsPerPage(Number(e.target.value))}
                            className="bg-transparent text-sm font-medium text-gray-900 focus:outline-none cursor-pointer"
                        >
                            <option value={12}>12</option>
                            <option value={24}>24</option>
                            <option value={48}>48</option>
                        </select>
                    </div>
                </div>

                {/* Content Grid */}
                {mode === 'creators' ? (
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
                        {displayItems.map((creator: Creator) => {
                            const coverSrc = creator.cover || defaultCover;
                            const avatarSrc = creator.avatar || DEFAULT_AVATAR_URL;

                            return (
                                <div
                                    key={creator.id}
                                    onClick={() => navigate(`/profile/${creator.id}`)}
                                    className="group cursor-pointer overflow-hidden rounded-2xl border border-gray-100 bg-white transition-all duration-300 hover:-translate-y-1 hover:shadow-[0_22px_48px_rgba(15,23,42,0.10)]"
                                >
                                    <div className="relative h-36 overflow-hidden bg-gray-100">
                                        <img
                                            src={coverSrc}
                                            alt={`${creator.name} 封面`}
                                            className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105"
                                            onError={(event) => {
                                                event.currentTarget.src = defaultCover;
                                            }}
                                        />
                                        <div className="absolute inset-0 bg-gradient-to-b from-black/5 via-transparent to-black/10" />
                                        <div className="absolute top-3 right-3">
                                            <FollowButton userId={creator.id} />
                                        </div>
                                    </div>
                                    <div className="relative px-6 pb-6">
                                        <div className="absolute -top-10 h-20 w-20 rounded-full bg-white p-1 shadow-[0_12px_28px_rgba(15,23,42,0.14)]">
                                            <img
                                                src={avatarSrc}
                                                alt={`${creator.name} 头像`}
                                                className="h-full w-full rounded-full object-cover"
                                                onError={(event) => {
                                                    event.currentTarget.src = DEFAULT_AVATAR_URL;
                                                }}
                                            />
                                            {creator.isOfficial ? (
                                                <span className="absolute bottom-0 right-0 flex h-6 min-w-6 items-center justify-center rounded-full border-[3px] border-white bg-amber-500 px-1 text-[10px] font-bold leading-none text-white shadow-sm" title="官方用户" aria-label="官方用户">
                                                    官
                                                </span>
                                            ) : creator.enterpriseVerified ? (
                                                <span className="absolute bottom-0 right-0 flex h-6 w-6 items-center justify-center rounded-full border-[3px] border-white bg-yellow-500 text-white shadow-sm" title="企业认证" aria-label="企业认证">
                                                    <ShieldCheck className="h-3.5 w-3.5" />
                                                </span>
                                            ) : creator.verified ? (
                                                <span className="absolute bottom-0 right-0 flex h-6 w-6 items-center justify-center rounded-full border-[3px] border-white bg-blue-500 text-white shadow-sm" title="实名认证" aria-label="实名认证">
                                                    <BadgeCheck className="h-3.5 w-3.5" />
                                                </span>
                                            ) : null}
                                        </div>
                                        <div className="pt-12">
                                            <h3 className="truncate text-lg font-bold text-gray-900">{creator.name}</h3>
                                            <p className="mb-4 truncate text-sm text-gray-500">@{creator.handle}</p>

                                        <div className="flex flex-wrap gap-2 mb-4">
                                            {creator.tags.slice(0, 3).map((tag: string) => (
                                                <span key={tag} className="px-2 py-1 bg-gray-50 text-xs text-gray-600 rounded-md">{tag}</span>
                                            ))}
                                        </div>

                                        <div className="flex items-center justify-between pt-4 border-t border-gray-50">
                                            <div className="text-xs text-gray-500">
                                                <strong className="text-gray-900 text-sm block">{creator.followers}</strong>
                                                粉丝
                                            </div>
                                            <div className="text-xs text-gray-500 text-right">
                                                <strong className="text-gray-900 text-sm block">{creator.likes}</strong>
                                                获赞
                                            </div>
                                            <div className="text-xs text-gray-500 text-right">
                                                <strong className="text-gray-900 text-sm block">
                                                    {creator.reviewCount > 0 ? creator.rating.toFixed(1) : '暂无'}
                                                </strong>
                                                评分
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                            );
                        })}
                    </div>
                ) : (
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                        {displayItems.map((brief: any) => (
                            <div
                                key={brief.id}
                                onClick={() => handleBriefClick(brief)}
                                className="bg-white p-6 rounded-2xl border border-gray-100 hover:shadow-lg transition-all cursor-pointer group flex flex-col"
                            >
                                <div className="flex justify-between items-start mb-4">
                                    <span className="px-3 py-1 bg-blue-50 text-blue-600 text-xs font-bold rounded-full">招募中</span>
                                    <span className="text-sm text-gray-500">{brief.deadline || '可谈'}</span>
                                </div>
                                <h3 className="font-bold text-gray-900 text-lg mb-2 group-hover:text-blue-600 transition-colors line-clamp-2">{brief.title}</h3>
                                <div className="text-xl font-bold text-gray-900 mb-4">{brief.budget || '可谈'}</div>

                                <div className="flex flex-wrap gap-2 mb-6">
                                    {brief.tags.map((tag: string) => (
                                        <span key={tag} className="px-2 py-1 bg-gray-50 text-xs text-gray-600 rounded-md">{tag}</span>
                                    ))}
                                </div>

                                <div className="mt-auto pt-4">
                                    <div className="border-t border-gray-50 mb-3"></div>
                                    <div className="flex items-center gap-3">
                                    <span
                                        className={`flex items-center gap-3 ${(brief as any).clientUserId ? 'cursor-pointer hover:opacity-80 transition-opacity' : ''}`}
                                        onClick={(e) => { if ((brief as any).clientUserId) { e.stopPropagation(); navigate(`/profile/${(brief as any).clientUserId}`); } }}
                                    >
                                    <img
                                        src={brief.avatar?.replace(/^http:\/\//, 'https://') || DEFAULT_AVATAR_URL}
                                        alt={brief.client}
                                        className="w-8 h-8 rounded-full object-cover bg-gray-100"
                                        onError={(e) => { e.currentTarget.src = DEFAULT_AVATAR_URL; }}
                                    />
                                    <div className="flex-1 flex items-center justify-between gap-3">
                                        <span className="text-sm text-gray-600">{brief.client}</span>
                                        <span className="text-xs text-gray-500">试稿 {brief.trialCount || 0}</span>
                                    </div>
                                    </span>
                                </div>
                                </div>
                            </div>
                        ))}
                    </div>
                )}

                {/* Pagination */}
                {totalPages > 1 && (
                    <div className="mt-12 flex justify-center items-center gap-2">
                        <button
                            onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                            disabled={currentPage === 1}
                            className={`p-2 rounded-lg border flex items-center justify-center transition-colors ${currentPage === 1 ? 'border-gray-100 text-gray-300 cursor-not-allowed' : 'border-gray-200 text-gray-600 hover:border-black hover:text-black'}`}
                        >
                            <ChevronLeft className="w-5 h-5" />
                        </button>

                        {Array.from({ length: totalPages }, (_, i) => i + 1).map(page => (
                            <button
                                key={page}
                                onClick={() => setCurrentPage(page)}
                                className={`w-10 h-10 rounded-lg text-sm font-medium transition-colors ${currentPage === page
                                    ? 'bg-black text-white'
                                    : 'text-gray-600 hover:bg-gray-100'}`}
                            >
                                {page}
                            </button>
                        ))}

                        <button
                            onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
                            disabled={currentPage === totalPages}
                            className={`p-2 rounded-lg border flex items-center justify-center transition-colors ${currentPage === totalPages ? 'border-gray-100 text-gray-300 cursor-not-allowed' : 'border-gray-200 text-gray-600 hover:border-black hover:text-black'}`}
                        >
                            <ChevronRight className="w-5 h-5" />
                        </button>
                    </div>
                )}


                {/* Empty States */}
                {displayItems.length === 0 && (
                    <div className="py-20 text-center">
                        <div className="w-20 h-20 bg-gray-50 rounded-full flex items-center justify-center mx-auto mb-4">
                            <Search className="w-8 h-8 text-gray-300" />
                        </div>
                        <h3 className="text-lg font-bold text-gray-900 mb-2">未找到相关结果</h3>
                        <p className="text-gray-500">尝试更换搜索关键词或筛选条件</p>
                    </div>
                )}
            </div>

            {/* Brief Detail Modal */}
            {selectedBrief && (
                <BriefDetailModal
                    isOpen={!!selectedBrief}
                    onClose={() => setSelectedBrief(null)}
                    brief={selectedBrief}
                    isOwnProfile={false} // Viewing others' briefs
                    onSave={() => { }}
                />
            )}
        </div>
    );
};

export default Discover;
